import { motion } from "framer-motion";
import { Building2, Users, Star } from "lucide-react";
import type { NihaoBridgeContent } from "../content/types";

interface PostTypesSectionProps {
  content: NihaoBridgeContent["postTypes"];
}

const iconMap = {
  building: Building2,
  people: Users,
  star: Star,
};

const colorSchemes = {
  cyan: {
    border: "border-cyan-500",
    icon: "text-cyan-500",
    hoverBorder: "hover:border-cyan-400",
  },
  green: {
    border: "border-emerald-500",
    icon: "text-emerald-500",
    hoverBorder: "hover:border-emerald-400",
  },
  amber: {
    border: "border-amber-500",
    icon: "text-amber-500",
    hoverBorder: "hover:border-amber-400",
  },
};

export default function PostTypesSection({ content }: PostTypesSectionProps) {
  return (
    <section className="w-full py-20 px-[5%] bg-white" id="post-types">
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-3xl lg:text-4xl font-bold mb-12 text-center text-slate-900"
        >
          {content.title}
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.types.map((postType, index) => {
            const IconComponent = iconMap[postType.icon];
            const colors = colorSchemes[postType.colorScheme];
            return (
              <motion.div
                key={postType.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className={`border-2 ${colors.border} ${colors.hoverBorder} rounded-xl p-8 text-center transition-all duration-300 hover:shadow-lg bg-white`}
              >
                <div className={`text-5xl mb-4 ${colors.icon}`}>
                  <IconComponent className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-slate-900">
                  {postType.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {postType.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
